/**
 * Unit — boundary contract schemas.
 *
 * Owned here, not beside the route: `apps/api/src/routes/manager/unit.schemas.ts` imports
 * and re-exports these to register the routes, and `apps/landlord/src/lib/leasing.api.ts` /
 * `apps/landlord/src/lib/portfolio.api.ts` derive their unit types from the same objects.
 * See packages/api-types/README.md.
 *
 * Covers:
 *   GET   /landlord/api/units                (UnitListItem within `units`)
 *   POST  /landlord/api/units                (CreateUnitData request body)
 *   PATCH /landlord/api/units/:id            (UpdateUnitData request body)
 *   GET   /landlord/api/units/:id            (metadataSchema.fields -> MetadataSchemaField)
 */
import { z } from 'zod';
const uuid = z.uuid();
// Unit occupancy status (computed from leases) — mirrored as a plain Zod enum, not
// cross-imported from unit.schemas.ts's UnitOccupancySchema (which remains the source of
// truth for UnitDetailSchema/UnitResponseSchema, out of scope for this migration). Must
// match the values UnitOccupancySchema accepts.
const UnitOccupancySchema = z.enum(['available', 'occupied']);
// ---------------------------------------------------------------------------
// Unit list item response
// ---------------------------------------------------------------------------
export const UnitListItemSchema = z.object({
    id: uuid,
    propertyId: uuid,
    unitNumber: z.string().nullable(),
    address1: z.string(),
    address2: z.string().nullable(),
    city: z.string(),
    state: z.string(),
    zipCode: z.string(),
    bedrooms: z.number().nullable(),
    bathrooms: z.string().max(5).nullable(),
    sqft: z.number().nullable(),
    occupancy: UnitOccupancySchema,
    propertyName: z.string(),
});
// ---------------------------------------------------------------------------
// Metadata schema field — a single jurisdiction-resolved unit metadata field definition
// ---------------------------------------------------------------------------
export const MetadataSchemaFieldSchema = z.object({
    name: z.string(),
    type: z.enum(['number', 'boolean', 'enum', 'set', 'string']),
    label: z.string(),
    description: z.string(),
    required: z.boolean(),
    recommended: z.boolean(),
    requiredReason: z.string().nullable(),
    currentValue: z.unknown().nullable(),
    validation: z.object({
        min: z.number().optional(),
        max: z.number().optional(),
        values: z.array(z.string()).optional(),
        maxLength: z.number().optional(),
    }),
    display: z.object({
        inputType: z.enum(['number', 'checkbox', 'radio', 'select', 'multiselect', 'text']),
        placeholder: z.string().optional(),
        helpText: z.string().optional(),
        labels: z.record(z.string(), z.string()).optional(),
        radioLabels: z.record(z.string(), z.string()).optional(),
    }),
});
// ---------------------------------------------------------------------------
// Create unit — POST /landlord/api/units (request body)
// ---------------------------------------------------------------------------
export const CreateUnitRequestSchema = z.object({
    propertyId: uuid,
    unitNumber: z
        .string()
        .min(1, 'Unit number cannot be empty if provided')
        .max(50, 'Unit number must be 50 characters or less')
        .optional(),
    address1: z
        .string()
        .min(1, 'Address is required')
        .max(500, 'Address must be 500 characters or less'),
    address2: z.string().max(500, 'Address line 2 must be 500 characters or less').optional(),
    city: z.string().min(1, 'City is required').max(100, 'City must be 100 characters or less'),
    state: z.string().min(1, 'State is required').max(50, 'State must be 50 characters or less'),
    zipCode: z
        .string()
        .min(1, 'ZIP code is required')
        .max(20, 'ZIP code must be 20 characters or less'),
    bedrooms: z
        .number()
        .int('Bedrooms must be a whole number')
        .min(0, 'Bedrooms cannot be negative')
        .max(20, 'Bedrooms cannot exceed 20')
        .optional(),
    bathrooms: z.string().max(5, 'Bathrooms must be 5 characters or less').optional(),
    sqft: z
        .number()
        .int('Square footage must be a whole number')
        .min(0, 'Square footage cannot be negative')
        .max(100000, 'Square footage cannot exceed 100,000')
        .optional(),
    metadata: z.record(z.string(), z.unknown()).optional(),
});
// ---------------------------------------------------------------------------
// Update unit — PATCH /landlord/api/units/:id (request body)
// ---------------------------------------------------------------------------
export const UpdateUnitRequestSchema = z.object({
    unitNumber: z
        .string()
        .min(1, 'Unit number cannot be empty if provided')
        .max(50, 'Unit number must be 50 characters or less')
        .nullable()
        .optional(),
    address1: z
        .string()
        .min(1, 'Address cannot be empty')
        .max(500, 'Address must be 500 characters or less')
        .optional(),
    address2: z
        .string()
        .max(500, 'Address line 2 must be 500 characters or less')
        .nullable()
        .optional(),
    city: z
        .string()
        .min(1, 'City cannot be empty')
        .max(100, 'City must be 100 characters or less')
        .optional(),
    state: z
        .string()
        .min(1, 'State cannot be empty')
        .max(50, 'State must be 50 characters or less')
        .optional(),
    zipCode: z
        .string()
        .min(1, 'ZIP code cannot be empty')
        .max(20, 'ZIP code must be 20 characters or less')
        .optional(),
    bedrooms: z
        .number()
        .int('Bedrooms must be a whole number')
        .min(0, 'Bedrooms cannot be negative')
        .max(20, 'Bedrooms cannot exceed 20')
        .nullable()
        .optional(),
    bathrooms: z.string().max(5, 'Bathrooms must be 5 characters or less').nullable().optional(),
    sqft: z
        .number()
        .int('Square footage must be a whole number')
        .min(0, 'Square footage cannot be negative')
        .max(100000, 'Square footage cannot exceed 100,000')
        .nullable()
        .optional(),
    metadata: z.record(z.string(), z.unknown()).optional(),
});
// ---------------------------------------------------------------------------
// Address autocomplete — GET /landlord/api/units/address-autocomplete (response)
// ---------------------------------------------------------------------------
export const AddressSuggestionSchema = z.object({
    address1: z.string(),
    city: z.string(),
    state: z.string(),
    zipCode: z.string(),
    displayLabel: z.string(),
});
// testMode=true when GEOAPIFY_API_KEY is absent (fixture suggestions returned)
export const AddressAutocompleteResponseSchema = z.object({
    suggestions: z.array(AddressSuggestionSchema),
    testMode: z.boolean(),
});
//# sourceMappingURL=unit.js.map